/**
 * Sort an Array of Objects by a property of those Objects.
 * Note: The sorting won't work correctly when trying to sort a 0 (Integer)
 *
 * @param {Array} objArray The Array of Objects to sort
 * @param {String} prop The property Key to sort on
 * @param {Integer} direction The direction of the sort. Ascending (1) or Descending (-1). Default is 1.
 * @returns Array A copy of the sorted Array``
 * @author https://stackoverflow.com/a/4698083
 */
export const sortByProperty = (objArray, prop, direction = 1) => {
    if (!Array.isArray(objArray)) throw new Error("FIRST ARGUMENT NOT AN ARRAY");

    var clone = objArray.slice(0),
        direct = direction,
        propPath = (prop.constructor===Array) ? prop : prop.split(".");

    clone.sort(function(a,b){
        for (var p in propPath){
            if (a[propPath[p]] && b[propPath[p]]){
                a = a[propPath[p]];
                b = b[propPath[p]];
            }
        }
        return ( (a < b) ? -1*direct : ((a > b) ? 1*direct : 0) );
    });
    return clone;
};

/**
 * Generate a (mostly) unique ID in an RFC4122 version 4 compliant format
 *
 * @returns String
 *
 * @source https://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid
 */
export const uuidv4 = () => {
  return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
    (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
  );
};

/**
 * Add unique IDs to each of the feed items
 *
 * @param {Array} dataArray Array of feed data Objects
 *
 * @returns Modified Array
 */
export const addIdsToData = (dataArray) => {
  let dataWithIds = dataArray;

  for (var i = 0, j = dataWithIds.length; i < j; ++i) {
    dataWithIds[i].id = uuidv4();
  }

  return dataWithIds;
};

/**
 * Add unique IDs and simplified wind direction data to each of the feed items
 *
 * @param {Array} dataArray Array of feed data Objects
 *
 * @returns Modified Array
 */
export const enrichData = (dataArray) => {
  let modifiedData = dataArray;
  let stationWindDirectionDegrees = 0;
  let stationAirQualityCategory = null;
  let stationDeterminingPollutant = null;

  for (var i = 0, j = modifiedData.length; i < j; ++i) {
    // Set a unique ID
    modifiedData[i].id = uuidv4();

    // Normalize the wind direction value
    stationWindDirectionDegrees = normalizeWDR(modifiedData[i].WDR);

    modifiedData[i].WindDirectionDegrees = stationWindDirectionDegrees;

    // Calculate the wind direction abbreviation
    modifiedData[i].WindDirectionCompass = degreesToCompassDirection(stationWindDirectionDegrees);

    // Normalize the wind speed value
    modifiedData[i].WindSpeed = normalizeGeneric(modifiedData[i].WSP);

    // Normalize the determining pollutant value
    modifiedData[i].DeterminingPollutantValue = normalizeGeneric(modifiedData[i].DeterminingPollutantValue);

    // Normalize the region rating value
    modifiedData[i].ContributeToNewRegionalAQC = normalizeGeneric(modifiedData[i].ContributeToNewRegionalAQC);

    // Normalize the air quality category
    stationAirQualityCategory = modifiedData[i].AirQualityCategory;

    if (stationAirQualityCategory === "N/A" || stationAirQualityCategory === "null" || stationAirQualityCategory === null || stationAirQualityCategory === "undefined" || stationAirQualityCategory === "") {
      modifiedData[i].AirQualityCategory = "Inactive";
    }

    // Add a sortable option for the air quality category
    modifiedData[i].AirQualityCategorySortable = sortableAirQualityCategory(modifiedData[i].AirQualityCategory);

    // Set the unit of measurement of the determining pollutant
    stationDeterminingPollutant = modifiedData[i].DeterminingPollutant;

    if (stationDeterminingPollutant !== "null" && stationDeterminingPollutant !== null && stationDeterminingPollutant !== "undefined") {
      switch (stationDeterminingPollutant) {
        case "PM2.5":
        case "PM2.5d":
        case "PM10":
          modifiedData[i].DeterminingPollutantUnit = "µg/m³";
         break;

        case "OZONE":
          modifiedData[i].DeterminingPollutantUnit = "pphm";
         break;

        default:
          break;
      }
    } else {
      // No valid value, so set a standard null value
      modifiedData[i].DeterminingPollutant = null;
    }
  }

  function normalizeWDR (wdr) {
    // Clean up the wind direction data to get rid of invalid values and round off the direction value
    let windDirection = null;

    if (wdr !== "" && wdr !== "undefined" && wdr !== "null" && wdr !== null) {
      windDirection = Math.round(wdr);
    }

    return windDirection;
  }

  function normalizeGeneric (val, isNumber) {
    // Clean up a provided value so that invalid values are returned as null
    let genericValue = val;

    if (genericValue === "" || genericValue === undefined || genericValue === "undefined" || genericValue === "null" || genericValue === null) {
      genericValue = null;
    }

    if (isNumber) {
      if (!genericValue) {
        // We can't sort by 0 or null, so we set a negative number for those values
        genericValue = -1;
      } else {
        genericValue = parseFloat(genericValue);
      }
    }

    return genericValue;
  }

  function sortableAirQualityCategory (val) {
    let sortableCategory = null;

    switch (val) {
      case "GOOD":
        sortableCategory = 1;
        break;

      case "FAIR":
        sortableCategory = 2;
        break;

      case "POOR":
        sortableCategory = 3;
        break;

      case "VERY POOR":
        sortableCategory = 4;
        break;

      case "EXTREMELY POOR":
        sortableCategory = 5;
        break;

      default:
        sortableCategory = -1;
        break;
    }

    return sortableCategory;
  }

  return modifiedData;
};

/**
 * Take an Array of data and remove items that do not meet the filtering criteria
 *
 * @param {Array} sourceArray The Array of Objects
 * @param {String} filterProperty The property of the Array child Objects to filter the outage data by. e.g. "postcode" or "outageType"
 * @param {String} filterValue The value to match in the filter property. If this value does not match, the Object is excluded from the result
 * @param {Integer} limit Optional. A limit to enforce on the returned data. e.g. Set this to 1, if you only want one result in your filtered Array
 * @param {Boolean} inverse Optional. Invert the condition matching. i.e. If true, then return all items that DO NOT match the provided filterValue
 *
 * @returns Array The filtered data
 */
export const filterArrayByProperty = (sourceArray, filterProperty, filterValue, limit = 0, inverse = false) => {
  let filteredOutageData = [];

  // Filter data by provided value
  if (filterValue !== "" && filterValue !== 0) {
    for (var i = 0, j = sourceArray.length; i < j; ++i) {
      if (limit !== 0 && filteredOutageData.length === limit) {
        break;
      } else {
        if (inverse) {
          if (sourceArray[i][filterProperty] !== filterValue) {
            filteredOutageData.push(sourceArray[i]);
          }
        } else {
          if (sourceArray[i][filterProperty] === filterValue) {
            filteredOutageData.push(sourceArray[i]);
          }
        }
      }
    }
  } else {
    // If no filter value was provided (or if it was 0 - e.g. if filtering by postcode)
    // then we return the entire source Array
    return sourceArray;
  }

  return filteredOutageData;
};

/**
 * Take an Array of data and remove items that do not meet the date filtering criteria
 * This is used to restrict results to a date range
 *
 * @param {Array} sourceArray The Array of Objects
 * @param {String} filterProperty The property of the outage data to use when filtering outages by a date comparision. e.g. "startDateTimeU"
 * @param {Integer} futureDateUnix A datetime value in Unix time
 *
 * @returns Array The filtered data
 */
export const filterArrayByDate = (sourceArray, filterProperty, futureDateUnix) => {
  let filteredOutageData = [];

  // Filter data by provided value
  if (futureDateUnix !== 0) {
    for (var i = 0, j = sourceArray.length; i < j; ++i) {
      if (sourceArray[i][filterProperty] < futureDateUnix) {
        filteredOutageData.push(sourceArray[i]);
      }
    }
  } else {
    // If no filter value was provided (or if it was 0)
    // then we return the entire source Array
    return sourceArray;
  }

  return filteredOutageData;
};

/**
 * Take the current local date and work out the Unix Epoch time at 12am of a provided number of days in the future
 *
 * @param {Integer} numDays The number of days in the future to calculate
 *
 * @returns Integer Unix time of 12:00am at X days in the future
 *
 * @source https://flaviocopes.com/how-to-get-tomorrow-date-javascript/
 */
export const midnightFutureDayUnixTime = (numDays) => {
  const today = new Date();
  const future = new Date(today);
  // Add the provided X day(s) to the current date
  future.setDate(future.getDate() + numDays);
  // Set the time to be 12am
  future.setHours(0,0,0,0);
  // Return the Unix time (JavaScript gives us the value in milliseconds)
  return future.valueOf() / 1000;
};

/**
 * Convert the outage data dates (UTC) into local time and then into a human-friendly format (HH:MM AM/PM DD/MM/YY)
 * e.g. 2021-05-18T07:32:46Z --> 5:32pm 18/05/21
 *
 * @param {String} dateTime Date to convert in the format of: YYYY-MM-DDTHH:MM:SSZ. e.g. 2021-05-18T07:32:46Z
 * @param {Boolean} includeTime Whether or not to include the time stamp with the date
 *
 * @returns String The human-friendly version of the datetime
 */
export const friendlyDateTime = (dateTime, includeTime = true) => {
  const localDateTimeArray = new Date(dateTime).toLocaleString().split(', ');
  const dateString = localDateTimeArray[0];
  const timeArray = localDateTimeArray[1].split(':');
  let timeString = "";
  let meridiem = "pm";
  let hourInteger = 0;

  // Just return the date if the time isn't needed.
  if (!includeTime) {
    return dateString;
  }

  // Check if the local time is in 24h format
  // If the time value ends in "m" (for am/pm)
  if (localDateTimeArray[1].substring(localDateTimeArray[1].length - 1).toLowerCase() === "m") {
    // This is 12h time

    // Capture the time as is
    timeString = localDateTimeArray[1];

    // Set the meridiem value to lowercase
    timeString = timeString.toLowerCase();

    // Remove any spaces
    timeString = timeString.replace(' ', '');
  } else {
    // This is 24h time

    // Remove the leading zero from the time
    if (timeArray[0].indexOf('0') === 0 && timeArray[0] !== "00") {
      timeArray[0] = timeArray[0].substring(1);
    }

    // Convert the hour into a number so we can perform further checks
    hourInteger = parseInt(timeArray[0]);

    // Set the ante meridiem
    if (hourInteger < 12) {
      meridiem = "am";
    }

    // Convert to 12 hour time
    if (hourInteger > 12) {
      hourInteger = hourInteger - 12;
    }

    // Set the correct hour for 12am.
    if (hourInteger === 0) {
      hourInteger = 12;
    }

    timeString = `${hourInteger.toString()}:${timeArray[1]}${meridiem}`;
  }

  return `${timeString} ${dateString}`;
};

/**
 * Take the provided outage data and combine the outages that have the same latitude and longitude values
 * into a single Object. This is necessary so that the map clustering can work correctly
 * (i.e. so that pins are not placed directly on top of each other).
 *
 * Note: This necessarily modifies the structure of the original data Array.
 *
 * @param {Array} outageData Outage data from the data feed endpoint
 *
 * @returns Array Outage data merged based on lat/lng coords
 */
export const mergeDuplicateOutages = (outageData) => {
  const dataLength = outageData.length; // Capture this here so we don't recalculate with each iteration
  let modifiedData = [];
  let uniqueData = true;
  let outageDataRecord = {};
  let modifiedDataRecord = {};

  // Loop over the outage data
  for (var i = 0, j = dataLength; i < j; ++i) {
    // Reset the possible unique data flag
    uniqueData = true;

    // Capture this here so we don't recalculate with each check
    outageDataRecord = outageData[i];

    // Loop over the already captured data to find outages with the same coordinates
    for (var k = 0, f = modifiedData.length; k < f; ++k) {
      // Capture this here so we don't recalculate with each check
      modifiedDataRecord = modifiedData[k];

      if (modifiedDataRecord.outages[0].latitude === outageDataRecord.latitude &&
        modifiedDataRecord.outages[0].longitude === outageDataRecord.longitude) {
        // This is a match in the existing data, so add it to the already captured outage data
        modifiedDataRecord.outages.push(outageDataRecord);
        uniqueData = false;
      }
    }

    // If there were no existing records that matched the criteria above, then capture this new outage data
    if (uniqueData) {
      modifiedData.push({
        "id": uuidv4(), // The ID needed for REACT to efficiently track updates
        "postcode": outageDataRecord.postcode, // The postcode needed for filtering the results
        "outages": [
          outageData[i]
        ]
      });
    }
  }

  return modifiedData;
};

/**
 * Check through a given query string and return all values that match a provided key.
 *
 * @param {String} queryString The full query string (or URL). e.g. id=1234&src=home&campaign-ID=r569_WW&f.Type|type=abc&f.Type|type=xyz
 * @param {String} key The key of the options to be matched. e.g. f.Type|type
 *
 * @returns Array of matched values
 */
export const getQueryParameterValuesByKey = (queryString, key) => {

  let queryKeyValues = [],
      allQueryParameters = [],
      currentParameter = [],
      s_EscapedQueryKey = '',
      valueTest = {};

  // Split all the query values into an Array
  allQueryParameters = queryString.split('&');

  // Escape any special characters in the query key
  s_EscapedQueryKey = key.replace(/\./g, "\\.").replace(/\|/g, "%7C").replace(/\+/g, "\\+");

  // Assemble the RegExp
  valueTest = new RegExp('(?=^|&)' + s_EscapedQueryKey + '=(.*?)(?=&|$)');

  // Check all the parameters for a key match and extract the value
  for (var i = 0, j = allQueryParameters.length; i < j; ++i) {

    currentParameter = allQueryParameters[i].match(valueTest);

    if (currentParameter !== null) {
      queryKeyValues.push(currentParameter[1]);
    }
  }

  return queryKeyValues;
};

/**
 * Update the station data by adding a new Key-Value pair to each station that shows it's relative distance from a point on the map
 *
 * @param {Object} originPoint Key-Value pairs of "lat" (latitude) and "lng" (longitude); where the value is a Number
 * @param {Array} sourceData Array of station data Objects. It is assumed that each will have "Latitude" and "Longitude" Key-Value pairs
 *
 * @returns Array The modified data
 */
export const addRelativeDistanceToStationData = (originPoint, sourceData) => {
  let modifiedStationData = [];
  let updatedStation = null;
  let distanceToOrigin = 0;

  // Loop over each of the stations and calculate their relative distance from the provided origin
  for (let i = 0, j = sourceData.length; i < j; ++i) {
    // Capture the existing data
    updatedStation = sourceData[i];

    // Add the new relative distance property
    distanceToOrigin = getHaversineDistance(originPoint, {
      "lat": parseFloat(updatedStation.Latitude),
      "lng": parseFloat(updatedStation.Longitude)
    });

    // Here we fix a sorting issue where if the relative distance is exactly 0 then the station lists are not sorted correctly.
    // i.e. the station of distance 0 gets ignored by the sorting function
    if (distanceToOrigin === 0) {
      updatedStation.RelativeDistanceToOrigin = 0.1;
    } else {
      updatedStation.RelativeDistanceToOrigin = distanceToOrigin;
    }

    modifiedStationData.push(updatedStation);
  }

  // Sort the modified data by distance from the provided origin
  modifiedStationData = sortByProperty(modifiedStationData, "RelativeDistanceToOrigin");

  return modifiedStationData;
};

/**
 * Calculate the distance between two points on the map
 *
 * @param {Object} mk1 Key-Value pairs of "lat" (latitude) and "lng" (longitude); where the value is a Number
 * @param {Object} mk2 Key-Value pairs of "lat" (latitude) and "lng" (longitude); where the value is a Number
 *
 * @usage
 * getHaversineDistance( {"lat": -33.93175, "lng": 151.24278}, {"lat": -33.86433, "lng": 151.16395} )
 *
 * @returns Number The distance (in kilometres) between the two points
 *
 * @source https://cloud.google.com/blog/products/maps-platform/how-calculate-distances-map-maps-javascript-api
 */
export const getHaversineDistance = (mk1, mk2) => {
  let R = 6371.0710; // Radius of the Earth in kilometres
  let rlat1 = mk1.lat * (Math.PI/180); // Convert degrees to radians
  let rlat2 = mk2.lat * (Math.PI/180); // Convert degrees to radians
  let difflat = rlat2-rlat1; // Radian difference (latitudes)
  let difflon = (mk2.lng-mk1.lng) * (Math.PI/180); // Radian difference (longitudes)

  let d = 2 * R * Math.asin(Math.sqrt(Math.sin(difflat/2)*Math.sin(difflat/2)+Math.cos(rlat1)*Math.cos(rlat2)*Math.sin(difflon/2)*Math.sin(difflon/2)));

  return d;
};

/**
 * Get the approximate cardinal or ordinal direction based on a 16-wind compass rose
 * e.g. 90 = east (E), 180 = south (S), 195 = south-southwest (SSW), 250 = west-southwest (WSW), 315 = northwest (NW)
 *
 * @param {Integer} degrees The direction in degrees
 *
 * @returns String Abbreviation of the wind direction
 */
export const degreesToCompassDirection = (degrees) => {
  let windDirection = '';

  // We have a tolerance of about 10 degrees around every point.
  switch (true) {
    // Approx. north
    case (degrees >= 0 && degrees <= 10):
    case (degrees >= 350 && degrees <= 360):
      windDirection = 'N';
      break;

    // Approx. north-northeast
    case (degrees > 10 && degrees < 35):
      windDirection = 'NNE';
      break;

    // Approx. northeast
    case (degrees >= 35 && degrees <= 55):
      windDirection = 'NE';
      break;

    // Approx. east-northeast
    case (degrees > 55 && degrees < 80):
      windDirection = 'ENE';
      break;

    // Approx. east
    case (degrees >= 80 && degrees <= 110):
      windDirection = 'E';
      break;

    // Approx. east-southeast
    case (degrees > 110 && degrees < 125):
      windDirection = 'ESE';
      break;

    // Approx. southeast
    case (degrees >= 125 && degrees <= 145):
      windDirection = 'SE';
      break;

    // Approx. south-southeast
    case (degrees > 145 && degrees < 170):
      windDirection = 'SSE';
      break;

    // Approx. south
    case (degrees >= 170 && degrees <= 190):
      windDirection = 'S';
      break;

    // Approx. south-southwest
    case (degrees > 190 && degrees < 215):
      windDirection = 'SSW';
      break;

    // Approx. southwest
    case (degrees >= 215 && degrees <= 235):
      windDirection = 'SW';
      break;

    // Approx. west-southwest
    case (degrees > 235 && degrees < 260):
      windDirection = 'WSW';
      break;

    // Approx. west
    case (degrees >= 260 && degrees <= 280):
      windDirection = 'W';
      break;

    // Approx. west-northwest
    case (degrees > 280 && degrees < 305):
      windDirection = 'WNW';
      break;

    // Approx. northwest
    case (degrees >= 305 && degrees <= 325):
      windDirection = 'NW';
      break;

    // Approx. north-northwest
    case (degrees > 325 && degrees < 350):
      windDirection = 'NNW';
      break;

    default:
      break;
  }

  return windDirection;
};

/**
 * Given any number, round it to the specified number of decimal places. Rounding is done to the nearest value
 * e.g. 5 = 5, 1.234 = 1.2, 8.889 = 8.9, 0.244 = 0.2, -12.887 = -12.9
 *
 * @param {Number} value The number to apply the rounding to
 * @param {Integer} decimals The number of decimal places to round the number to
 *
 * @returns Number The provided value, rounded to the specified number of decimal places
 *
 * @source https://www.jacklmoore.com/notes/rounding-in-javascript/
 */
export const roundToDecimal = (value, decimals) => {
  return Number(Math.round(value+'e'+decimals)+'e-'+decimals);
}

/**
 * Apply changes to the provided value so that map data values are consistent
 * e.g. decimal places
 *
 * @param {Number} value The number to standardize
 *
 * @returns String A standardized version of the provided value
 */
export const standardizeValue = (value) => {
  let modifiedValue = value;

  if (Math.sign(modifiedValue) !== -1) {
    // Round the number to 1 decimal place
    modifiedValue = roundToDecimal(modifiedValue, 1).toString();
  } else {
    // Is a negative number, so just return 0.0
    modifiedValue = '0.0';
  }

  if (modifiedValue === '0') {
    // Convert zero values to one decimal place
    modifiedValue = '0.0';
  }

  if (modifiedValue.indexOf('.') === -1) {
    // Convert whole values to one decimal place
    modifiedValue = `${modifiedValue}.0`;
  }

  return modifiedValue;
}